import type { IntlShape } from 'react-intl';
import { serviceBrand } from '../../outreachServiceBrand';
import type { OutreachActivityEvent, SentPitch } from '../../outreachTypes';
import { replyLabelText } from '../replyLabelText';
import { listenDuration } from '../liveFormat';
import { detailsMessages as m } from './detailsMessages';

export type TimelineDot = 'done' | 'ok' | 'next' | 'todo';

/** One line of the timeline of a pitch (design outreach-v2 t2, t3). */
export interface TimelineStep {
  key: string;
  /** The event kind, or `notOpened` / `followUpNext` for a step that did not happen yet. */
  kind: OutreachActivityEvent['kind'] | 'notOpened' | 'followUpNext';
  dot: TimelineDot;
  title: string;
  detail: string | null;
  /** ISO time; null for a step that did not happen. */
  at: string | null;
}

/** The events of a pitch; an API from before `events` answers none. */
function eventsOf(pitch: SentPitch): OutreachActivityEvent[] {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an older API answers no field
  return pitch.events ?? [];
}

/** One event as a step. `found` and `ready` are lines of the artist, not of a pitch: no step. */
function eventStep(intl: IntlShape, event: OutreachActivityEvent, pitch: SentPitch): TimelineStep | null {
  const base = { key: event.id, kind: event.kind, at: event.at, dot: 'done' as TimelineDot, detail: null };
  switch (event.kind) {
    case 'sent':
      return {
        ...base,
        title: intl.formatMessage(m.stepSent),
        detail: pitch.fromEmail === null ? null : intl.formatMessage(m.stepSentFrom, { email: pitch.fromEmail }),
      };
    case 'followUp':
      return { ...base, title: intl.formatMessage(m.stepFollowUpSent) };
    case 'opened':
      return { ...base, title: intl.formatMessage(m.stepOpened) };
    case 'listened':
      return {
        ...base,
        title: intl.formatMessage(m.stepListened, { duration: listenDuration(intl, event.listenedSeconds ?? 0) }),
      };
    case 'clicked':
      return { ...base, title: intl.formatMessage(m.stepClicked, { service: serviceBrand(event.service ?? '').name }) };
    case 'replied':
      return {
        ...base,
        dot: 'ok',
        title: intl.formatMessage(m.stepReplied),
        detail: event.replyLabel === null ? null : intl.formatMessage(replyLabelText(event.replyLabel)),
      };
    case 'placed':
      return {
        ...base,
        dot: 'ok',
        title:
          event.count === null
            ? intl.formatMessage(m.stepPlaced)
            : intl.formatMessage(m.stepPlacedAt, { position: event.count }),
      };
    case 'found':
    case 'ready':
      return null;
  }
}

/**
 * The timeline of a sent pitch: what happened, oldest first, then what did not happen yet (not
 * opened, the waiting follow-up). A planned pitch has no timeline.
 */
export function pitchTimeline(intl: IntlShape, pitch: SentPitch): TimelineStep[] {
  if (pitch.planned) {
    return [];
  }
  const steps = eventsOf(pitch).flatMap((event) => {
    const step = eventStep(intl, event, pitch);
    return step === null ? [] : [step];
  });
  const has = (kind: string) => eventsOf(pitch).some((event) => event.kind === kind);
  if (!has('opened') && !has('listened') && !has('replied') && !has('placed')) {
    steps.push({
      key: 'notOpened',
      kind: 'notOpened',
      dot: 'todo',
      title: intl.formatMessage(m.stepNotOpened),
      detail: null,
      at: null,
    });
  }
  if (pitch.followUpAt !== null) {
    steps.push({
      key: 'followUpNext',
      kind: 'followUpNext',
      dot: 'next',
      title: intl.formatMessage(m.stepFollowUp, {
        date: intl.formatDate(new Date(pitch.followUpAt), { month: 'short', day: 'numeric' }),
      }),
      detail: intl.formatMessage(m.stepFollowUpSub),
      at: null,
    });
  }
  return steps;
}

/** The newest human reply of the pitch: the reply box of the status card (design t3). */
export function newestReply(pitch: SentPitch): OutreachActivityEvent | null {
  const replies = eventsOf(pitch).filter((event) => event.kind === 'replied');
  return replies[replies.length - 1] ?? null;
}

/** The longest listen of the pitch, in seconds; null with no listen. */
export function longestListen(pitch: SentPitch): number | null {
  const listens = eventsOf(pitch).flatMap((event) =>
    event.kind === 'listened' && event.listenedSeconds !== null ? [event.listenedSeconds] : []
  );
  return listens.length === 0 ? null : Math.max(...listens);
}
